import { useEffect, useState } from "react";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";
import type { VisitRow } from "@/lib/database.types";

interface Props {
  visit: VisitRow;
  size?: "sm" | "md";
}

function CalendarPlusIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4M12 14v4M10 16h4" />
    </svg>
  );
}

export function AddToCalendar({ visit, size = "sm" }: Props) {
  const [open, setOpen] = useState(false);

  const gcal = googleCalendarUrl(visit);
  if (!gcal) return null;
  if (visit.status === "cancelled" || visit.status === "no_show") return null;

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const buttonCls =
    size === "md"
      ? "inline-flex items-center gap-1.5 rounded-[10px] border border-fh-border bg-white px-3 py-2 text-[13px] font-semibold text-fh-ink hover:bg-fh-cream"
      : "inline-flex items-center gap-1 text-[12.5px] font-semibold text-fh-green hover:text-fh-green-dark";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={buttonCls}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <CalendarPlusIcon size={size === "md" ? 16 : 13} />
        Add to calendar
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute left-0 mt-1 z-40 w-56 bg-white border border-fh-border rounded-[10px] shadow-card py-1"
          >
            <a
              href={gcal}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-3.5 py-2 text-[13.5px] text-fh-ink hover:bg-fh-cream"
            >
              Google Calendar
              <span className="ml-1 text-fh-gray-light">↗</span>
            </a>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                downloadIcs(visit);
                setOpen(false);
              }}
              className="block w-full text-left px-3.5 py-2 text-[13.5px] text-fh-ink hover:bg-fh-cream"
            >
              Apple Calendar / Outlook
              <span className="ml-1 text-fh-gray-light">(.ics)</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
